import NumberInput from '../../components/NumberInput'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ArrowLeft, ArrowRight, Check, Loader2, Target, Scale, Calendar, Ruler,
  Droplet, User, Cake, Settings,
} from 'lucide-react'
import PhoenixLogo from '../../components/PhoenixLogo'
import { Button } from '../../components/ui/button'
import MobileBreadcrumb from '../../components/MobileBreadcrumb'
import { useUnits } from '../../contexts/UnitsContext'
import { cn } from '../../lib/utils'

const TOTAL_STEPS = 11

const EDIT_PROFILE_CRUMBS = [{ key: 'breadcrumb.editProfile', to: null }]
function EditProfileBreadcrumb() {
  return <MobileBreadcrumb manualCrumbs={EDIT_PROFILE_CRUMBS} />
}

// Steps that allow Skip:
const SKIPPABLE_STEPS = new Set([6, 7, 8, 9])

const WATER_PRESETS = [2000, 2500, 3000]

function todayIso() {
  return new Date().toLocaleDateString('en-CA')
}

export default function OnboardingWizard({ profile, onComplete, editing = false }) {
  const { t } = useTranslation()
  const { unitSystem, setUnitSystem, weightUnit, lengthUnit, weightToKg, lengthToCm, displayValueWeight, displayValueLength, formatWeight } = useUnits()

  const initialStep = editing ? 2 : 1
  const [step, setStep] = useState(initialStep)
  const [submitting, setSubmitting] = useState(false)

  const seedWeight = profile?.start_weight != null ? String(displayValueWeight(profile.start_weight).toFixed(1)) : ''
  const seedGoal = profile?.goal_weight != null ? String(displayValueWeight(profile.goal_weight).toFixed(1)) : ''
  const seedHeight = profile?.height_cm != null ? String(displayValueLength(profile.height_cm).toFixed(1)) : ''

  const [name, setName] = useState(profile?.name || '')
  const [birthday, setBirthday] = useState(profile?.birthday || '')
  const [startWeight, setStartWeight] = useState(seedWeight)
  const [startDate, setStartDate] = useState(profile?.start_date || todayIso())
  const [goalWeight, setGoalWeight] = useState(seedGoal)
  const [goalDate, setGoalDate] = useState(profile?.goal_date || '')
  const [sex, setSex] = useState(profile?.sex || '')
  const [height, setHeight] = useState(seedHeight)
  const [pickedUnitSystem, setPickedUnitSystem] = useState(unitSystem)
  const [waterGoalMl, setWaterGoalMl] = useState(profile?.water_goal_ml ?? 3000)

  const startKg = startWeight ? weightToKg(Number(startWeight)) : null
  const goalKg = goalWeight ? weightToKg(Number(goalWeight)) : null
  const heightCm = height ? lengthToCm(Number(height)) : null

  const totalToLose = startKg != null && goalKg != null ? startKg - goalKg : null
  const goalWarning = startKg != null && goalKg != null && goalKg >= startKg

  const pacePreview = useMemo(() => {
    if (!goalDate || startKg == null || goalKg == null) return null
    const target = new Date(goalDate)
    const today = new Date(); today.setHours(0, 0, 0, 0)
    const weeks = (target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24 * 7)
    if (weeks <= 0) return null
    const ratePerWeekKg = (startKg - goalKg) / weeks
    return { weeks: Math.round(weeks * 10) / 10, ratePerWeekKg }
  }, [goalDate, startKg, goalKg])

  const canAdvance = (() => {
    if (step === 2) return name.trim().length > 0
    if (step === 3) return Boolean(birthday)
    if (step === 4) return startKg != null && startKg > 0
    if (step === 5) return goalKg != null && goalKg > 0 && !goalWarning
    return true
  })()

  const next = () => setStep((s) => Math.min(TOTAL_STEPS, s + 1))
  const prev = () => setStep((s) => Math.max(editing ? 2 : 1, s - 1))

  const skipStep = () => {
    if (step === 6) setGoalDate('')
    if (step === 7) setSex('')
    if (step === 8) setHeight('')
    // Step 9 (units) — skip just leaves the current pickedUnitSystem; no clear
    next()
  }

  const handleSubmit = async () => {
    setSubmitting(true)

    // Apply unit picker as a side effect (localStorage-only for now)
    if (pickedUnitSystem !== unitSystem) {
      setUnitSystem(pickedUnitSystem)
    }

    const ok = await onComplete({
      name: name.trim() || null,
      birthday: birthday || null,
      sex: sex || null,
      startWeight: startKg,
      startDate: startDate || null,
      goalWeight: goalKg,
      goalDate: goalDate || null,
      heightCm,
      waterGoalMl,
    })
    setSubmitting(false)
    return ok
  }

  return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center py-8 content-fade">
      {editing && (
        <EditProfileBreadcrumb />
      )}
      {step > 1 && (
        <div
          className="flex items-center gap-1.5 mb-8"
          aria-label={t('phoenixTracker.onboarding.progress.stepOf', { current: step - 1, total: TOTAL_STEPS - 1 })}
        >
          {Array.from({ length: TOTAL_STEPS - 1 }, (_, i) => i + 2).map((s) => (
            <span
              key={s}
              className={cn(
                'h-2 rounded-full transition-all',
                s < step ? 'w-2 bg-app-primary-500' : s === step ? 'w-6 bg-app-primary-500' : 'w-2 bg-muted'
              )}
            />
          ))}
        </div>
      )}

      <div className="w-full max-w-md">
        {step === 1 && <WelcomeStep onNext={next} t={t} />}

        {step === 2 && (
          <TextStep
            icon={User}
            title={t('phoenixTracker.onboarding.name.title')}
            subtitle={t('phoenixTracker.onboarding.name.subtitle')}
            placeholder={t('phoenixTracker.onboarding.name.placeholder')}
            value={name}
            onChange={setName}
            maxLength={60}
          />
        )}

        {step === 3 && (
          <DateStep
            icon={Cake}
            title={t('phoenixTracker.onboarding.birthday.title')}
            subtitle={t('phoenixTracker.onboarding.birthday.subtitle')}
            value={birthday}
            onChange={setBirthday}
            max={todayIso()}
          />
        )}

        {step === 4 && (
          <NumberStep
            icon={Scale}
            title={t('phoenixTracker.onboarding.startWeight.title')}
            subtitle={t('phoenixTracker.onboarding.startWeight.subtitle')}
            unit={weightUnit}
            value={startWeight}
            onChange={setStartWeight}
            min="20"
            max="700"
            extra={(
              <div className="mt-3">
                <label className="block text-xs text-muted-foreground mb-1">
                  {t('phoenixTracker.onboarding.startWeight.dateLabel')}
                </label>
                <input
                  type="date"
                  value={startDate}
                  max={todayIso()}
                  onChange={(e) => setStartDate(e.target.value)}
                  dir="ltr"
                  className="h-12 sm:h-11 w-full px-3 rounded-lg border border-input bg-background text-foreground font-mono text-base sm:text-sm focus:outline-none"
                />
              </div>
            )}
          />
        )}

        {step === 5 && (
          <NumberStep
            icon={Target}
            title={t('phoenixTracker.onboarding.goalWeight.title')}
            unit={weightUnit}
            value={goalWeight}
            onChange={setGoalWeight}
            min="20"
            max="700"
            extra={
              totalToLose != null ? (
                <p className={cn('mt-3 text-sm', goalWarning ? 'text-red-500' : 'text-app-primary-500 font-medium')}>
                  {goalWarning
                    ? t('phoenixTracker.onboarding.goalWeight.warningSameOrHigher')
                    : t('phoenixTracker.onboarding.goalWeight.preview', {
                        amount: formatWeight(totalToLose),
                        unit: weightUnit,
                      })}
                </p>
              ) : null
            }
          />
        )}

        {step === 6 && (
          <DateStep
            icon={Calendar}
            title={t('phoenixTracker.onboarding.goalDate.title')}
            subtitle={t('phoenixTracker.onboarding.goalDate.subtitle')}
            value={goalDate}
            onChange={setGoalDate}
            min={todayIso()}
            extra={
              pacePreview ? (
                <p className="mt-3 text-sm text-app-primary-500 font-medium">
                  {t('phoenixTracker.onboarding.goalDate.preview', {
                    weeks: pacePreview.weeks,
                    rate: formatWeight(pacePreview.ratePerWeekKg),
                    unit: weightUnit,
                  })}
                </p>
              ) : null
            }
          />
        )}

        {step === 7 && (
          <ChoiceStep
            icon={User}
            title={t('phoenixTracker.onboarding.sex.title')}
            subtitle={t('phoenixTracker.onboarding.sex.subtitle')}
            options={[
              { value: 'male', label: t('phoenixTracker.onboarding.sex.male') },
              { value: 'female', label: t('phoenixTracker.onboarding.sex.female') },
              { value: 'prefer_not', label: t('phoenixTracker.onboarding.sex.preferNot') },
            ]}
            value={sex}
            onChange={setSex}
          />
        )}

        {step === 8 && (
          <NumberStep
            icon={Ruler}
            title={t('phoenixTracker.onboarding.height.title')}
            subtitle={t('phoenixTracker.onboarding.height.subtitle')}
            unit={lengthUnit}
            value={height}
            onChange={setHeight}
            min="50"
            max="300"
          />
        )}

        {step === 9 && (
          <ChoiceStep
            icon={Settings}
            title={t('phoenixTracker.onboarding.units.title')}
            subtitle={t('phoenixTracker.onboarding.units.subtitle')}
            options={[
              { value: 'metric', label: t('phoenixTracker.onboarding.units.metric') },
              { value: 'imperial', label: t('phoenixTracker.onboarding.units.imperial') },
            ]}
            value={pickedUnitSystem}
            onChange={setPickedUnitSystem}
          />
        )}

        {step === 10 && (
          <WaterStep value={waterGoalMl} onChange={setWaterGoalMl} t={t} />
        )}

        {step === 11 && (
          <SummaryStep
            t={t}
            name={name}
            birthday={birthday}
            sex={sex}
            startWeight={startWeight}
            goalWeight={goalWeight}
            goalDate={goalDate}
            height={height}
            waterGoalMl={waterGoalMl}
            weightUnit={weightUnit}
            lengthUnit={lengthUnit}
            unitSystem={pickedUnitSystem}
          />
        )}

        {step > 1 && (
          <div className="mt-8 flex items-center gap-2">
            {step > 2 && (
              <Button type="button" variant="outline" onClick={prev} className="flex-1 sm:flex-none">
                <ArrowLeft className="w-4 h-4 me-1.5 rtl:-scale-x-100" />
                {t('phoenixTracker.onboarding.nav.back')}
              </Button>
            )}

            {SKIPPABLE_STEPS.has(step) && (
              <Button type="button" variant="ghost" onClick={skipStep} className="text-muted-foreground">
                {t('phoenixTracker.onboarding.nav.skip')}
              </Button>
            )}

            {step < TOTAL_STEPS && (
              <Button
                type="button"
                onClick={next}
                disabled={!canAdvance}
                className="ms-auto bg-app-primary-500 hover:bg-app-primary-600 text-white"
              >
                {t('phoenixTracker.onboarding.nav.next')}
                <ArrowRight className="w-4 h-4 ms-1.5 rtl:-scale-x-100" />
              </Button>
            )}

            {step === TOTAL_STEPS && (
              <Button
                type="button"
                onClick={handleSubmit}
                disabled={submitting}
                className="ms-auto bg-app-primary-500 hover:bg-app-primary-600 text-white"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin me-1.5" /> : <Check className="w-4 h-4 me-1.5" />}
                {editing ? t('phoenixTracker.onboarding.nav.saveChanges') : t('phoenixTracker.onboarding.done.cta')}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

// --- Step components ---

function WelcomeStep({ onNext, t }) {
  return (
    <div className="text-center">
      <PhoenixLogo className="w-20 h-20 mx-auto mb-6" />
      <h1 className="text-2xl font-bold text-foreground mb-2">
        {t('phoenixTracker.onboarding.welcome.title')}
      </h1>
      <p className="text-muted-foreground mb-8">
        {t('phoenixTracker.onboarding.welcome.subtitle')}
      </p>
      <Button onClick={onNext} className="bg-app-primary-500 hover:bg-app-primary-600 text-white px-8">
        {t('phoenixTracker.onboarding.welcome.cta')}
        <ArrowRight className="w-4 h-4 ms-1.5 rtl:-scale-x-100" />
      </Button>
    </div>
  )
}

function StepHeader({ icon: Icon, title, subtitle, iconColor = 'orange' }) {
  const colorClass = iconColor === 'blue'
    ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-500'
    : 'bg-app-primary-100 dark:bg-app-primary-900/30 text-app-primary-500'
  return (
    <div className="flex items-center gap-3 mb-4">
      <div className={cn('rounded-full p-2.5', colorClass.split(' ').slice(0, 2).join(' '))}>
        <Icon className={cn('w-5 h-5', colorClass.split(' ').slice(2).join(' '))} />
      </div>
      <div className="flex-1">
        <h2 className="text-lg font-semibold text-foreground">{title}</h2>
        {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
      </div>
    </div>
  )
}

function TextStep({ icon, title, subtitle, placeholder, value, onChange, maxLength }) {
  return (
    <div>
      <StepHeader icon={icon} title={title} subtitle={subtitle} />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        maxLength={maxLength}
        autoFocus
        className="h-14 sm:h-11 w-full px-3 rounded-lg border border-input bg-background text-foreground text-base focus:outline-none"
      />
    </div>
  )
}

function NumberStep({ icon, title, subtitle, unit, value, onChange, min, max, extra }) {
  return (
    <div>
      <StepHeader icon={icon} title={title} subtitle={subtitle} />
      <div className="flex items-center gap-2">
        <NumberInput
          inputMode="decimal"
          step="any"
          min={min}
          max={max}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoFocus
          className="h-14 sm:h-11 flex-1 px-3 rounded-lg border border-input bg-background text-foreground font-mono text-2xl sm:text-xl focus:outline-none"
        />
        <span className="text-sm font-medium text-muted-foreground">{unit}</span>
      </div>
      {extra}
    </div>
  )
}

function DateStep({ icon, title, subtitle, value, onChange, min, max, extra }) {
  return (
    <div>
      <StepHeader icon={icon} title={title} subtitle={subtitle} />
      <input
        type="date"
        value={value}
        min={min}
        max={max}
        onChange={(e) => onChange(e.target.value)}
        dir="ltr"
        className="h-14 sm:h-11 w-full px-3 rounded-lg border border-input bg-background text-foreground font-mono text-base sm:text-sm focus:outline-none"
      />
      {extra}
    </div>
  )
}

function ChoiceStep({ icon, title, subtitle, options, value, onChange }) {
  return (
    <div>
      <StepHeader icon={icon} title={title} subtitle={subtitle} />
      <div className="grid gap-2">
        {options.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            className={cn(
              'h-12 rounded-lg text-sm font-medium transition-colors text-start px-4 border',
              value === opt.value
                ? 'border-app-primary-500 bg-app-primary-50 dark:bg-app-primary-950/30 text-foreground'
                : 'border-input bg-background text-muted-foreground hover:bg-accent'
            )}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  )
}

function WaterStep({ value, onChange, t }) {
  return (
    <div>
      <StepHeader
        icon={Droplet}
        iconColor="blue"
        title={t('phoenixTracker.onboarding.waterGoal.title')}
        subtitle={t('phoenixTracker.onboarding.waterGoal.subtitle')}
      />
      <div className="grid grid-cols-3 gap-2 mb-3">
        {WATER_PRESETS.map((preset) => {
          const label = preset === 2000
            ? t('phoenixTracker.onboarding.waterGoal.preset2L')
            : preset === 2500
              ? t('phoenixTracker.onboarding.waterGoal.preset25')
              : t('phoenixTracker.onboarding.waterGoal.preset3')
          return (
            <button
              key={preset}
              type="button"
              onClick={() => onChange(preset)}
              className={cn(
                'h-12 rounded-lg text-sm font-medium transition-colors',
                value === preset
                  ? 'bg-blue-500 text-white'
                  : 'bg-muted text-muted-foreground hover:bg-accent'
              )}
            >
              {label}
            </button>
          )
        })}
      </div>
      <NumberInput
        inputMode="numeric"
        step="50"
        min="500"
        max="10000"
        value={value}
        onChange={(e) => onChange(Number(e.target.value) || 0)}
        className="h-14 sm:h-11 w-full px-3 rounded-lg border border-input bg-background text-foreground font-mono text-base sm:text-sm focus:outline-none"
      />
      <p className="text-xs text-muted-foreground mt-1">mL</p>
    </div>
  )
}

function SummaryStep({ t, name, birthday, sex, startWeight, goalWeight, goalDate, height, waterGoalMl, weightUnit, lengthUnit, unitSystem }) {
  const sexLabel = sex
    ? t(`phoenixTracker.onboarding.sex.${sex === 'prefer_not' ? 'preferNot' : sex}`)
    : '—'
  const rows = [
    { icon: User, label: t('phoenixTracker.onboarding.name.title'), value: name || '—' },
    { icon: Cake, label: t('phoenixTracker.onboarding.birthday.title'), value: birthday || '—' },
    { icon: Scale, label: t('phoenixTracker.onboarding.startWeight.title'), value: startWeight ? `${startWeight} ${weightUnit}` : '—' },
    { icon: Target, label: t('phoenixTracker.onboarding.goalWeight.title'), value: goalWeight ? `${goalWeight} ${weightUnit}` : '—' },
    { icon: Calendar, label: t('phoenixTracker.onboarding.goalDate.title'), value: goalDate || '—' },
    { icon: User, label: t('phoenixTracker.onboarding.sex.title'), value: sexLabel },
    { icon: Ruler, label: t('phoenixTracker.onboarding.height.title'), value: height ? `${height} ${lengthUnit}` : '—' },
    { icon: Settings, label: t('phoenixTracker.onboarding.units.title'), value: t(`phoenixTracker.onboarding.units.${unitSystem}`) },
    { icon: Droplet, label: t('phoenixTracker.onboarding.waterGoal.title'), value: `${(waterGoalMl / 1000).toFixed(2)} L` },
  ]
  return (
    <div>
      <h2 className="text-lg font-semibold text-foreground mb-1">{t('phoenixTracker.onboarding.done.title')}</h2>
      <p className="text-sm text-muted-foreground mb-4">{t('phoenixTracker.onboarding.done.summaryHeader')}</p>
      <div className="space-y-2">
        {rows.map((r, i) => (
          <div key={i} className="flex items-center gap-3 p-3 rounded-lg border border-border bg-card">
            <r.icon className="w-4 h-4 text-app-primary-500 flex-shrink-0" />
            <span className="text-xs text-muted-foreground flex-1 truncate">{r.label}</span>
            <span className="text-sm font-mono font-medium text-foreground truncate">{r.value}</span>
          </div>
        ))}
      </div>
    </div>
  )
}
